import React, { useState } from "react";
import ChannelIcon from "./ChannelIcon";
import ChannelTopicsDrawer from "./ChannelTopicsDrawer";
import { getClusterByDisplayName } from "../constants/topicClusters";
import Tooltip from "./Tooltip";

const ChannelTopicsTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'percentage', direction: 'desc' });
  const [selectedRow, setSelectedRow] = useState(null);

  // Info icon SVG component
  const InfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-info-circle ml-2 text-grey-60 cursor-help">
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
      <path d="M12 9h.01" />
      <path d="M11 12h1v4h1" />
    </svg>
  );

  // Don't render table if no data
  if (!data || data.length === 0) {
    return null;
  }

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (typeof aValue === 'string') {
      return sortConfig.direction === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return sortConfig.direction === 'asc' 
      ? aValue - bValue 
      : bValue - aValue;
  });

  // Get the appropriate sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const getClusterDotColor = (clusterName) => {
    const c = getClusterByDisplayName(clusterName);
    return c?.color ?? '#6B7280';
  };

  // Function to format cluster display with percentage
  const formatClusterDisplay = (clusterName, percentage) => {
    return `${clusterName} (${percentage}%)`;
  };

  const openDrawer = (row) => setSelectedRow(row);

  return (
    <div>
      <h3 className="text-md font-normal text-black-0 px-10 py-3">Channel topics</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-grey-20 table-fixed">
          <thead>
            <tr>
              <th 
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0 w-40"
                onClick={() => handleSort('channel')}
              >
                Channel {getSortDirectionIndicator('channel')}
              </th>
              <th 
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0 w-64"
                onClick={() => handleSort('topTopic')}
              >
                <div className="flex items-center">
                  <span>Cluster distribution {getSortDirectionIndicator('topTopic')}</span>
                  <Tooltip
                    content="Shows the dominant topic cluster for each channel with its percentage of total channel messages."
                    placement="bottom"
                    align="start"
                  >
                    <span className="inline-flex cursor-help">
                      <InfoIcon />
                    </span>
                  </Tooltip>
                </div>
              </th>
              <th className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60">
                <div className="flex items-center">
                  <span>Topics distribution</span>
                  <Tooltip
                    content="Top topics by share of channel mentions. Click a row for the full list and axis scores."
                    placement="bottom"
                    align="start"
                  >
                    <span className="inline-flex cursor-help">
                      <InfoIcon />
                    </span>
                  </Tooltip>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-20">
            {sortedData.map((row) => (
              <tr
                key={row.channel}
                className="hover:bg-grey-10/80 cursor-pointer"
                onClick={() => openDrawer(row)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openDrawer(row);
                  }
                }}
                tabIndex={0}
                aria-label={`View details for ${row.channel}`}
              >
                <td className="pl-10 pr-5 py-3 text-sm font-medium text-black-0 w-40">
                  <div className="flex items-center gap-1 truncate">
                    <ChannelIcon isPrivate={row.is_private} size={14} />
                    <span className="truncate">{row.channel}</span>
                  </div>
                </td>
                <td className="pl-10 pr-5 py-3 w-64">
                  <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-white text-black-0 border border-grey-20 flex items-center gap-1 inline-flex whitespace-nowrap">
                    <div 
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getClusterDotColor(row.topTopic) }}
                    ></div>
                    <span className="truncate">{formatClusterDisplay(row.topTopic, row.percentage)}</span>
                  </span>
                </td>
                <td className="pl-10 pr-5 py-3">
                  <div className="flex flex-wrap gap-1 max-w-none items-center">
                    {row.topics && row.topics.slice(0, 2).map((topic) => (
                      <span 
                        key={`${topic.name}-${topic.cluster}`}
                        className="px-2 py-0.5 rounded-md text-xs font-medium bg-white text-black-0 border border-grey-20 flex items-center gap-1 inline-flex max-w-[min(100%,280px)]"
                        title={`${topic.name}: ${topic.percentage}% of channel mentions`}
                      >
                        <div 
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{
                          backgroundColor: getClusterDotColor(
                            topic.clusterDisplayName || row.topTopic
                          )
                        }}
                        ></div>
                        <span className="truncate">{topic.name} ({topic.percentage}%{typeof topic.value === "number" ? ` · ${topic.value}` : ""})</span>
                      </span>
                    ))}
                    {row.topics && row.topics.length > 2 && (
                      <span className="px-2 py-0.5 rounded-md text-xs bg-grey-20 text-grey-60">
                        +{row.topics.length - 2} topics
                      </span>
                    )}
                    {(!row.topics || row.topics.length === 0) && (
                      <span className="px-2 py-0.5 rounded-md text-xs bg-grey-20 text-grey-60">
                        No specific topics identified
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ChannelTopicsDrawer
        isOpen={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        row={selectedRow}
      />
    </div>
  );
};

export default ChannelTopicsTable; 